// Canal « Sang » : absorbance propre à l'hémoglobine, calculée à partir de deux images de transmission de même grille
// de pixels (loi de Beer-Lambert) :
// - transmittance T = (I − noir) / (verre − noir) : « verre » = niveau du fond sans tissu, estimé sur l'image
//   (glassLevel) ; « noir » = décalage de la caméra (unités d'origine, 0 par défaut) ;
// - densité optique OD = −log10(T). Les OD s'additionnent : à 415 nm (bande de Soret de l'hémoglobine), sang +
//   diffusion et absorption du tissu ; au canal de référence (530 ou 625 nm), surtout la diffusion du tissu, plus
//   faible aux grandes longueurs d'onde (à 530 nm l'hémoglobine absorbe encore, environ 10 fois moins qu'à 415 nm :
//   contraste un peu réduit, pas de biais) ;
// - sang = OD(absorbant) − k × OD(référence) : k compense la diffusion plus forte à courte longueur d'onde. Estimé
//   (estimateBlood) comme la médiane du rapport des OD sur le tissu, la plupart des pixels ne contenant pas de sang :
//   le tissu sans sang tombe à zéro quelle que soit son épaisseur. Si le sang couvre plus de la moitié du tissu,
//   k est surestimé : à corriger à la main ;
// - affichage : sang ramené à la plage [seuil, maximum] (en OD) à travers une LUT ; sous le seuil, rien. Le seuil
//   estimé écarte la variabilité du tissu sans sang (médiane + 3 écarts robustes du signal sur le tissu : par
//   construction de k, la moitié du tissu est un peu au-dessus de zéro).
// Deux affichages :
// - carte de chaleur (par défaut) : le signal calculé une fois sur un niveau réduit de la pyramide (mailles de 2^niveau
//   px), lissé sur le tissu, en image semi-transparente au-dessus du tissu en pleine résolution (fond gris : OD de la
//   référence, calculée par le shader). Les réglages ne font que recolorer cette petite image (bloodHeatData, puis
//   heatField si k ou le lissage change, heatCanvas sinon) ;
// - pixel par pixel : tout calculé par le shader en pleine résolution, sur le fond gris. Les transmissions d'un même
//   setup sont décalées de quelques pixels par endroits (aberration chromatique latérale, variable d'une tuile du
//   stitching à l'autre) : fausses franges au bord des structures, que la carte de chaleur ne montre pas.
import { VivLayerExtension } from '@hms-dbmi/viv';

import { lutColor, lutGlsl } from './lut';
import { gaussBlur } from './blur';
import { pixelSize } from '../../services';

export const BLOOD_ID = 'blood';  // identifiant du canal parmi les modalités
// k, seuil et maximum à null : estimés automatiquement (estimateBlood) à la première ouverture et quand l'affichage
// ou la taille des mailles change ; en attendant, valeurs usuelles.
export const BLOOD_DEFAULTS = {
  k: null, odMin: null, odMax: null, context: 0.4, lut: 'fire', lutRange: null, dark: 0,
  display: 'heatmap', heatLevel: 7, heatSmooth: 1.5, opacity: 0.9,
};
export const BLOOD_FALLBACK = { k: 1.4, odMin: 0.02, odMax: 0.5 };

const modules = new Map();
// Module de shader pour une LUT (recompilé seulement quand la LUT change, comme les autres canaux) ; canaux :
// transmittances de l'absorbant et de la référence (contraste [noir, verre]). showBlood à 0 : fond gris seul (carte de
// chaleur affichée au-dessus).
function moduleFor(lutId) {
  if (modules.has(lutId)) return modules.get(lutId);
  const name = `bloodModule${modules.size}`;
  const module = {
    name,
    uniformTypes: {
      k: 'f32', odMin: 'f32', odMax: 'f32', context: 'f32', showBlood: 'f32', lutLo: 'f32', lutHi: 'f32',
    },
    fs: `
uniform ${name}Uniforms {
  float k;
  float odMin;
  float odMax;
  float context;
  float showBlood;
  float lutLo;
  float lutHi;
} ${name};
${lutGlsl('blood_lut', lutId)}

vec3 blood_color(float[NUM_CHANNELS] intensity) {
  float ods = -log(clamp(intensity[0], 0.001, 1.)) / log(10.);
  float odr = -log(clamp(intensity[1], 0.001, 1.)) / log(10.);
  float t = (ods - ${name}.k * odr - ${name}.odMin) / max(${name}.odMax - ${name}.odMin, 0.001);
  vec3 c = ${name}.showBlood > 0.5 && t > 0. ? blood_lut(mix(${name}.lutLo, ${name}.lutHi, clamp(t, 0., 1.))) : vec3(0.);  // sous le seuil : noir
  float structure = ${name}.context * clamp(odr / 0.5, 0., 1.);  // tissu en gris (OD de la référence, jusqu'à 0,5)
  return clamp(c + vec3(structure), 0., 1.);
}`,
    inject: { 'fs:DECKGL_MUTATE_COLOR': 'rgba = vec4(blood_color(intensity), 1.0);' },
  };
  modules.set(lutId, module);
  return module;
}
const extensionOf = layer => (layer.props.extensions || []).find(e => e instanceof BloodExtension);

// Réglages lus dans la prop « blood » du calque (k, seuil, maximum, fond, affichage) : un changement de curseur ne met
// à jour que les uniformes. La LUT est une option de l'extension : en changer crée une nouvelle extension.
export class BloodExtension extends VivLayerExtension {
  getVivShaderTemplates() { return { modules: [moduleFor(extensionOf(this)?.opts.lut)] }; }

  updateState(params, extension) {
    super.updateState(params, extension);
    const b = this.props.blood || {};
    const uniforms = {
      k: b.k ?? BLOOD_FALLBACK.k, odMin: b.odMin ?? BLOOD_FALLBACK.odMin, odMax: b.odMax ?? BLOOD_FALLBACK.odMax,
      context: b.context ?? 0, showBlood: b.display === 'pixels' ? 1 : 0,
      lutLo: b.lutRange?.[0] ?? 0, lutHi: b.lutRange?.[1] ?? 1,  // partie de la LUT utilisée
    };
    for (const model of this.getModels()) model.shaderInputs.setProps({ [moduleFor(extension.opts.lut).name]: uniforms });
  }
}
BloodExtension.extensionName = 'BloodExtension';

// Niveau réduit d'au moins minPixels pixels.
function smallLevel(loader, minPixels = 2e5) {
  let level = loader.length - 1;
  while (level > 0) {
    const { width, height } = pixelSize(loader[level]);
    if (width * height >= minPixels) break;
    level -= 1;
  }
  return level;
}
const raster = (loader, level) => loader[level].getRaster({ selection: { t: 0, z: 0, c: 0 } });
const odOf = ([lo, hi]) => v => -Math.log10(Math.min(1, Math.max(1e-3, (v - lo) / ((hi - lo) || 1))));
const TISSUE_OD = 0.03;  // tissu : OD de la référence au-dessus

// Seuil d'Otsu (256 classes entre le minimum et le maximum).
export function otsu(data) {
  let lo = Infinity;
  let hi = -Infinity;
  for (let i = 0; i < data.length; i += 1) { if (data[i] < lo) lo = data[i]; if (data[i] > hi) hi = data[i]; }
  const bins = new Float64Array(256);
  const span = (hi - lo) || 1;
  for (let i = 0; i < data.length; i += 1) bins[Math.min(255, Math.floor(((data[i] - lo) / span) * 256))] += 1;
  const total = data.length;
  let sum = 0;
  for (let b = 0; b < 256; b += 1) sum += b * bins[b];
  let w0 = 0;
  let sum0 = 0;
  let best = 0;
  let bestVar = -1;
  for (let b = 0; b < 256; b += 1) {
    w0 += bins[b];
    if (!w0 || w0 === total) continue;
    sum0 += b * bins[b];
    const m0 = sum0 / w0;
    const m1 = (sum - sum0) / (total - w0);
    const v = w0 * (total - w0) * (m0 - m1) ** 2;
    if (v > bestVar) { bestVar = v; best = b; }
  }
  return lo + ((best + 1) / 256) * span;
}

const median = values => {
  const sorted = Float64Array.from(values).sort();
  return sorted[Math.floor(sorted.length / 2)];
};

// Niveau du verre (fond sans tissu, le plus clair en transmission), dans les unités du chargeur : médiane des pixels
// au-dessus du seuil d'Otsu, sur un niveau réduit. Un éclairage inégal (vignettage) n'est pas corrigé.
export async function glassLevel(loader) {
  const { data } = await raster(loader, smallLevel(loader));
  const t = otsu(data);
  const bright = [];
  for (let i = 0; i < data.length; i += 1) if (data[i] > t) bright.push(data[i]);
  return bright.length ? median(bright) : null;
}

// k (médiane de OD(absorbant) / OD(référence) sur le tissu), seuil (médiane + 3 écarts robustes du signal de sang sur
// le tissu) et maximum (99,9e centile, au moins seuil + span) ; cal = { s: [noir, verre], r: [noir, verre] } dans les
// unités des chargeurs. Niveau : celui de la carte de chaleur, sinon (pixel par pixel) un niveau d'au moins 4 millions
// de pixels, avec span = 0,4 OD (en pleine résolution le sang, non dilué, dépasse nettement les valeurs de ce niveau).
// null si trop peu de tissu.
export async function estimateBlood(loaderS, loaderR, cal, { level = smallLevel(loaderS, 4e6), span = 0.4 } = {}) {
  const lv = Math.min(level, loaderS.length - 1);
  const [rs, rr] = await Promise.all([raster(loaderS, lv), raster(loaderR, lv)]);
  const odS = odOf(cal.s);
  const odR = odOf(cal.r);
  const s = [];
  const r = [];
  for (let i = 0; i < rs.data.length; i += 1) {
    const vr = odR(rr.data[i]);
    if (vr > TISSUE_OD) { s.push(odS(rs.data[i])); r.push(vr); }
  }
  if (s.length < 100) return null;
  const k = Math.min(5, Math.max(0.3, median(s.map((v, i) => v / r[i]))));
  const blood = Float64Array.from(s.map((v, i) => v - k * r[i])).sort();
  const mid = blood[Math.floor(blood.length / 2)];
  const mad = median(blood.map(v => Math.abs(v - mid)));
  const odMin = Math.max(0, mid + 3 * 1.4826 * mad);
  const odMax = Math.max(odMin + span, blood[Math.floor(blood.length * 0.999)]);
  const round = v => Math.round(v * 1000) / 1000;
  return { k: round(k), odMin: round(odMin), odMax: round(odMax) };
}

// ------------------------------------------------------------ carte de chaleur

// OD de l'absorbant et de la référence au niveau `level` (mailles de 2^level px pleine résolution), lues une fois :
// { width, height, cell, odS, odR }.
export async function bloodHeatData(loaderS, loaderR, cal, level) {
  const lv = Math.min(level, loaderS.length - 1);
  const [rs, rr] = await Promise.all([raster(loaderS, lv), raster(loaderR, lv)]);
  const odS = odOf(cal.s);
  const odR = odOf(cal.r);
  const n = rs.data.length;
  const out = { width: rs.width, height: rs.height, cell: 2 ** lv, odS: new Float32Array(n), odR: new Float32Array(n) };
  for (let i = 0; i < n; i += 1) { out.odS[i] = odS(rs.data[i]); out.odR[i] = odR(rr.data[i]); }
  return out;
}

// Champ de la carte de chaleur : sang = OD(absorbant) − k × OD(référence) sur le tissu, lissé (σ en mailles,
// convolution normalisée : le fond ne dilue pas le bord du tissu) ; NaN hors du tissu. Refait seulement si k ou le
// lissage change.
export function heatField(data, { k, heatSmooth }) {
  const { width: w, height: h, odS, odR } = data;
  const n = w * h;
  let blood = new Float32Array(n);
  let weight = new Float32Array(n);
  for (let i = 0; i < n; i += 1) {
    if (odR[i] > TISSUE_OD) { blood[i] = odS[i] - k * odR[i]; weight[i] = 1; }
  }
  const tissue = weight.slice();
  if (heatSmooth > 0) {
    blood = gaussBlur(blood, w, h, heatSmooth);
    weight = gaussBlur(weight, w, h, heatSmooth);
  }
  const field = new Float32Array(n);
  for (let i = 0; i < n; i += 1) field[i] = tissue[i] && weight[i] >= 0.2 ? blood[i] / weight[i] : NaN;
  return { width: w, height: h, field };
}

// Réglages de la carte de chaleur estimés sur ses mailles : k (médiane de OD(absorbant) / OD(référence) sur le tissu),
// puis, sur le champ lissé tel qu'affiché, seuil (médiane + 2 écarts robustes : les mailles nettement au-dessus du
// tissu sans sang) et maximum (99,9e centile, au moins seuil + 0,005 OD). En mailles, le sang est dilué : quelques
// centièmes d'OD. null si trop peu de tissu.
export function estimateHeat(data, { heatSmooth }) {
  const { odS, odR } = data;
  const ratios = [];
  for (let i = 0; i < odR.length; i += 1) if (odR[i] > TISSUE_OD) ratios.push(odS[i] / odR[i]);
  if (ratios.length < 100) return null;
  const k = Math.min(5, Math.max(0.3, median(ratios)));
  const values = Float64Array.from(heatField(data, { k, heatSmooth }).field.filter(v => !Number.isNaN(v))).sort();
  const mid = values[Math.floor(values.length / 2)];
  const mad = median(values.map(v => Math.abs(v - mid)));
  const odMin = Math.max(0, mid + 2 * 1.4826 * mad);
  const odMax = Math.max(odMin + 0.005, values[Math.floor(values.length * 0.999)]);
  const round = v => Math.round(v * 10000) / 10000;
  return { k: Math.round(k * 1000) / 1000, odMin: round(odMin), odMax: round(odMax) };
}

// Image de la carte de chaleur (canevas) : champ ramené à [seuil, maximum], coloré par la LUT ; transparent hors du
// tissu et sous le seuil, opacité croissante avec le signal jusqu'à `opacity` (un signal faible ne masque pas le
// tissu). Refaite à chaque changement de seuil, maximum, LUT ou opacité : une passe sur les mailles.
export function heatCanvas({ width: w, height: h, field }, { odMin, odMax, lut, lutRange, opacity }) {
  const colors = Array.from({ length: 256 }, (_, i) => lutColor(lut, [255, 255, 255], false, i / 255, lutRange));
  const alpha = Array.from({ length: 256 }, (_, i) => Math.round(255 * opacity * (i / 255) ** 0.6));
  const rgba = new Uint8ClampedArray(w * h * 4);
  const scale = 255 / Math.max(odMax - odMin, 1e-3);
  for (let i = 0; i < field.length; i += 1) {
    const v = field[i];
    if (!(v > odMin)) continue;  // hors du tissu (NaN) ou sous le seuil
    const u = Math.min(255, Math.round((v - odMin) * scale));
    const c = colors[u];
    rgba[4 * i] = c[0]; rgba[4 * i + 1] = c[1]; rgba[4 * i + 2] = c[2]; rgba[4 * i + 3] = alpha[u];
  }
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  canvas.getContext('2d').putImageData(new ImageData(rgba, w, h), 0, 0);
  return canvas;
}
